import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAnimation } from '../contexts/AnimationContext';
import api, { formatApiError, isAuthenticated, getCurrentUserProfile, verifyMfa, getTenantSubdomain } from '../services/api';
import RecaptchaWidget from './RecaptchaWidget';
import './Login.css';

const Login = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { useTransformersAnimation } = useAnimation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaTempToken, setMfaTempToken] = useState(null);
  const [mfaUser, setMfaUser] = useState(null);
  const [mfaMethod, setMfaMethod] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captchaSiteKey, setCaptchaSiteKey] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const emailInputRef = useRef(null);
  const videoRef = useRef(null);
  const navigateAfterVideoRef = useRef(null);
  const mfaCodeInputRef = useRef(null);

  // Redirect authenticated users based on their role
  useEffect(() => {
    const redirectAuthenticatedUser = async () => {
      if (isAuthenticated()) {
        try {
          const userData = await getCurrentUserProfile();
          const userRole = userData?.role;
          
          // Redirect based on user role
          if (userRole === 'EMPLOYE') {
            navigate('/employee-dashboard', { replace: true });
          } else if (userRole === 'MANAGER') {
            navigate('/manager-dashboard', { replace: true });
          } else if (userRole === 'ADMIN') {
            navigate('/admin-dashboard', { replace: true });
          } else {
            // Default to admin dashboard if role is unknown
            navigate('/admin-dashboard', { replace: true });
          }
        } catch (error) {
          // If we can't fetch user profile, default to admin dashboard
          console.error('Failed to fetch user profile:', error);
          navigate('/admin-dashboard', { replace: true });
        }
      }
    };
    
    redirectAuthenticatedUser();
  }, [navigate]);

  // #region agent log
  const handleEmailFocus = () => {
    setEmailFocused(true);
    // fetch('http://127.0.0.1:7243/ingest/9ade902d-2b24-40bc-817a-822b5ac0a127',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Login.js:handleEmailFocus',message:'Email input focused',data:{emailFocused:true},timestamp:Date.now(),sessionId:'debug-session',runId:'initial',hypothesisId:'A'})}).catch(()=>{});
  };
  const handleEmailBlur = () => {
    setEmailFocused(false);
    // fetch('http://127.0.0.1:7243/ingest/9ade902d-2b24-40bc-817a-822b5ac0a127',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Login.js:handleEmailBlur',message:'Email input blurred',data:{emailFocused:false,emailValue:email},timestamp:Date.now(),sessionId:'debug-session',runId:'initial',hypothesisId:'A'})}).catch(()=>{});
  };
  const handlePasswordFocus = () => {
    setPasswordFocused(true);
    // fetch('http://127.0.0.1:7243/ingest/9ade902d-2b24-40bc-817a-822b5ac0a127',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Login.js:handlePasswordFocus',message:'Password input focused',data:{passwordFocused:true},timestamp:Date.now(),sessionId:'debug-session',runId:'initial',hypothesisId:'B'})}).catch(()=>{});
  };
  const handlePasswordBlur = () => {
    setPasswordFocused(false);
    // fetch('http://127.0.0.1:7243/ingest/9ade902d-2b24-40bc-817a-822b5ac0a127',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Login.js:handlePasswordBlur',message:'Password input blurred',data:{passwordFocused:false,passwordValue:password},timestamp:Date.now(),sessionId:'debug-session',runId:'initial',hypothesisId:'B'})}).catch(()=>{});
  };
  // #endregion agent log

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requiresCaptcha && !recaptchaToken) {
      setError(t('completeCaptcha'));
      return;
    }
    setIsLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      const response = await api.login(email, password, recaptchaToken || undefined);
      setIsLoading(false);

      if (response.requires_mfa && response.temp_token) {
        setMfaRequired(true);
        setMfaTempToken(response.temp_token);
        setMfaUser(response.user);
        setMfaMethod(response.mfa_method || 'totp');
        setMfaCode('');
        setFieldErrors({});
        setTimeout(() => mfaCodeInputRef.current?.focus(), 100);
        return;
      }

      setIsLoginSuccess(true);
      navigateAfterVideoRef.current = () => {
        if (response.must_change_password) {
          navigate('/reset-password');
        } else {
          const userRole = response.user?.role;
          if (userRole === 'EMPLOYE') navigate('/employee-dashboard');
          else if (userRole === 'MANAGER') navigate('/manager-dashboard');
          else navigate('/admin-dashboard');
        }
      };
    } catch (err) {
      const formattedError = formatApiError(err);
      setFieldErrors(formattedError.fieldErrors);
      setError(formattedError.generalError);
      setRequiresCaptcha(formattedError.requiresCaptcha || false);
      setCaptchaSiteKey(formattedError.captchaSiteKey || null);
      setRecaptchaToken('');
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaTempToken || !mfaCode.trim()) {
      setFieldErrors({ code: [t('enterVerificationCode')] });
      return;
    }
    setMfaVerifying(true);
    setError(null);
    setFieldErrors({});
    try {
      const response = await verifyMfa(mfaTempToken, mfaCode.trim());
      setMfaVerifying(false);
      setMfaRequired(false);
      setMfaTempToken(null);
      setMfaCode('');
      setIsLoginSuccess(true);
      navigateAfterVideoRef.current = () => {
        if (response.must_change_password) {
          navigate('/reset-password');
        } else {
          const userRole = response.user?.role;
          if (userRole === 'EMPLOYE') navigate('/employee-dashboard');
          else if (userRole === 'MANAGER') navigate('/manager-dashboard');
          else navigate('/admin-dashboard');
        }
      };
    } catch (err) {
      const formattedError = formatApiError(err);
      setFieldErrors(formattedError.fieldErrors || { code: [formattedError.generalError || t('invalidCode')] });
      setError(formattedError.generalError);
      setMfaVerifying(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaTempToken(null);
    setMfaUser(null);
    setMfaMethod(null);
    setMfaCode('');
    setError(null);
    setFieldErrors({});
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Focus on email input after animation completes (only for transformers animation)
  useEffect(() => {
    if (useTransformersAnimation) {
      const timer = setTimeout(() => {
        if (emailInputRef.current) {
          emailInputRef.current.focus();
        }
      }, 600); // Wait for form container animation (0.5s) + small buffer

      return () => clearTimeout(timer);
    }
  }, [useTransformersAnimation]);

  // Play video animation when login is successful (non-RS tenants)
  useEffect(() => {
    if (isLoginSuccess && videoRef.current && getTenantSubdomain() !== 'rs') {
      videoRef.current.play().catch(err => {
        console.error('Error playing video:', err);
      });
    }
  }, [isLoginSuccess]);

  // RS tenant: trigger navigation after HTML animation (~3s)
  const isRsTenant = getTenantSubdomain() === 'rs';
  const authAccent = isRsTenant ? '#2a2a2a' : '#0045F5';
  const authFocusShadow = isRsTenant
    ? 'focus:shadow-[0_0_0_1px_rgba(42,42,42,1)]'
    : 'focus:shadow-[0_0_0_1px_rgba(0,69,245,1)]';
  useEffect(() => {
    if (isLoginSuccess && isRsTenant) {
      const timer = setTimeout(() => {
        if (navigateAfterVideoRef.current) {
          navigateAfterVideoRef.current();
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isLoginSuccess, isRsTenant]);

  // Handle video end event
  const handleVideoEnd = () => {
    // Wait 0.8 seconds to show the last frame, then navigate
    setTimeout(() => {
      if (navigateAfterVideoRef.current) {
        navigateAfterVideoRef.current();
      }
    }, 800);
  };

  // Render form container based on animation mode
  const FormContainer = useTransformersAnimation ? motion.div : 'div';
  const formContainerProps = useTransformersAnimation ? {
    layoutId: "form-container",
    layout: false,
    style: { willChange: 'height' }
  } : {};

  // Render header based on animation mode
  const HeaderContainer = useTransformersAnimation ? motion.div : 'div';
  const headerProps = useTransformersAnimation ? {
    layoutId: "header",
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.35, ease: 'easeOut' },
    style: { willChange: 'opacity' }
  } : {};

  // Render input wrapper based on animation mode
  const InputWrapper = useTransformersAnimation ? motion.div : 'div';
  const getInputWrapperProps = (fieldName, delay) => useTransformersAnimation ? {
    layoutId: `${fieldName}-input`,
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3, delay, ease: 'easeInOut' },
    style: { willChange: 'opacity' }
  } : {};

  // Render button based on animation mode
  const ButtonWrapper = useTransformersAnimation ? motion.button : 'button';
  const buttonProps = useTransformersAnimation ? {
    layoutId: "submit-button",
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3, delay: 0.2, ease: 'easeInOut' },
    style: { 
      background: authAccent,
      willChange: 'opacity' 
    }
  } : {
    style: { background: authAccent }
  };

  return (
    <>
      <main className="relative z-10 w-full px-0 py-0 md:px-4 md:py-12 md:max-w-[800px] h-screen md:h-auto">
        <div className="relative p-[2px] rounded-none md:rounded-2xl transition-all duration-500 w-full h-full md:h-auto transform group/card scale-100 md:scale-[0.8]" style={{ background: authAccent, transformOrigin: 'center center' }}>
          <FormContainer className="glass-card rounded-none md:rounded-2xl p-6 md:p-10 w-full h-full relative z-10 flex flex-col overflow-y-auto" {...formContainerProps}>
            {/* Login success animation: rs_logo_animation.html for RS tenant, video for others */}
            {isLoginSuccess && (
              <div
                className="absolute inset-0 flex items-center justify-center z-20 rounded-none md:rounded-2xl"
                style={{ backgroundColor: isRsTenant ? '#2a2a2a' : '#ffffff' }}
              >
                {isRsTenant ? (
                  <iframe
                    src="/rs_logo_animation.html"
                    title={t('logoAnimationTitle')}
                    className="w-full h-full border-0"
                    style={{ minWidth: '100%', minHeight: '100%' }}
                  />
                ) : (
                  <video
                    ref={videoRef}
                    className="object-contain"
                    style={{ width: '50%', height: '50%' }}
                    src="/login_animation.mp4"
                    autoPlay
                    muted
                    playsInline
                    onEnded={handleVideoEnd}
                  />
                )}
              </div>
            )}
            <div className={isLoginSuccess ? 'fade-out-content' : ''}>
              {mfaRequired ? (
                <>
                  <HeaderContainer className="text-center mb-2" {...headerProps}>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight mt-8 md:mt-0">{t('mfaTitle')}</h1>
                    <p className="text-gray-500 text-base">
                      {mfaMethod === 'email'
                        ? t('mfaEmailHint')
                        : t('mfaAppHint')}
                    </p>
                  </HeaderContainer>
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                  <form onSubmit={handleMfaSubmit} className="flex flex-col gap-5">
                    <div className="group relative">
                      <label className="block text-sm font-semibold text-nokia-blue mb-1.5 ml-1" htmlFor="mfa-code">
                        {t('verificationCode')}
                      </label>
                      <div className="relative flex items-center">
                        <span className="material-symbols-outlined absolute left-4 text-gray-400 z-10">key</span>
                        <input
                          ref={mfaCodeInputRef}
                          id="mfa-code"
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="000000"
                          maxLength={8}
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                          className={`w-full bg-white border-2 ${fieldErrors.code ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-base rounded-lg focus:ring-0 focus:border-nokia-blue block p-4 pl-12 placeholder-gray-400 h-12 ${authFocusShadow}`}
                          disabled={mfaVerifying}
                        />
                      </div>
                      {fieldErrors.code && (
                        <p className="mt-1 text-sm text-red-600 ml-1">{Array.isArray(fieldErrors.code) ? fieldErrors.code[0] : fieldErrors.code}</p>
                      )}
                    </div>
                    <div className="flex gap-3 mt-2">
                      <button
                        type="button"
                        onClick={handleBackToLogin}
                        className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        disabled={mfaVerifying}
                      >
                        {tCommon('back')}
                      </button>
                      <button
                        type="submit"
                        className={`flex-1 py-3 px-4 text-white font-bold rounded-lg hover:opacity-90 disabled:opacity-75 ${isRsTenant ? 'bg-[#2a2a2a]' : 'bg-[#0045F5]'}`}
                        disabled={mfaVerifying || mfaCode.trim().length < 4}
                      >
                        {mfaVerifying ? t('verifying') : t('verify')}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
              <HeaderContainer className="text-center mb-2" {...headerProps}>
                <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight mt-8 md:mt-0">{t('welcomeTitle')}</h1>
                <p className="text-gray-500 text-base">{t('welcomeSubtitle')}</p>
              </HeaderContainer>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-5 flex-grow">
              {/* Email input */}
              <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('email', 0.05)}>
                <label 
                  className={`block text-sm font-semibold text-nokia-blue mb-1.5 ml-1 transition-all duration-300 opacity-0 -translate-y-2 group-focus-within:opacity-100 group-focus-within:translate-y-0`} 
                  htmlFor="email"
                >
                  {tCommon('email')}
                </label>
                <div className="relative flex items-center">
                  <span className="material-symbols-outlined absolute left-4 text-gray-400 transition-colors group-focus-within:text-nokia-blue z-10">mail</span>
                  <input
                    ref={emailInputRef}
                    className={`w-full bg-white border-2 ${
                      fieldErrors.email ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base rounded-lg focus:ring-0 focus:border-nokia-blue block p-4 pl-12 placeholder-gray-400 transition-all duration-300 ease-out hover:border-gray-400 ${authFocusShadow} h-12`}
                    id="email"
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (fieldErrors.email) {
                        setFieldErrors({ ...fieldErrors, email: null });
                      }
                    }}
                    onFocus={handleEmailFocus}
                    onBlur={handleEmailBlur}
                    required
                    disabled={isLoading}
                  />
                </div>
                {fieldErrors.email && (
                  <p className="mt-1 text-sm text-red-600 ml-1">{fieldErrors.email[0]}</p>
                )}
              </InputWrapper>

              {/* Password input */}
              <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('password', 0.1)}>
                <label 
                  className={`block text-sm font-semibold text-nokia-blue mb-1.5 ml-1 transition-all duration-300 opacity-0 -translate-y-2 group-focus-within:opacity-100 group-focus-within:translate-y-0`} 
                  htmlFor="password"
                >
                  {tCommon('password')}
                </label>
                <div className="relative flex items-center">
                  <span className="material-symbols-outlined absolute left-4 text-gray-400 transition-colors group-focus-within:text-nokia-blue z-10">lock</span>
                  <input
                    className={`w-full bg-white border-2 ${
                      fieldErrors.password ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base rounded-lg focus:ring-0 focus:border-nokia-blue block p-4 pl-12 pr-12 placeholder-gray-400 transition-all duration-300 ease-out hover:border-gray-400 ${authFocusShadow} h-12`}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (fieldErrors.password) {
                        setFieldErrors({ ...fieldErrors, password: null });
                      }
                    }}
                    onFocus={handlePasswordFocus}
                    onBlur={handlePasswordBlur}
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? tCommon('hidePassword') : tCommon('showPassword')}
                    disabled={isLoading}
                  >
                    <span className="material-symbols-outlined">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="mt-1 text-sm text-red-600 ml-1">{fieldErrors.password[0]}</p>
                )}
              </InputWrapper>

              {/* reCAPTCHA when required after 5 failed attempts */}
              {requiresCaptcha && (
                captchaSiteKey ? (
                  <RecaptchaWidget
                    siteKey={captchaSiteKey}
                    onChange={(token) => setRecaptchaToken(token || '')}
                    theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                  />
                ) : (
                  <p className="text-amber-600 dark:text-amber-400 text-sm my-2">
                    {t('tooManyAttempts')}
                  </p>
                )
              )}

              {/* Forgot password */}
              <AnimatePresence>
                {useTransformersAnimation ? (
                  <motion.div 
                    className="flex justify-end"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3, delay: 0.15, ease: 'easeOut' }}
                    style={{ willChange: 'transform, opacity' }}
                  >
                    <Link to="/forgot-password" className="text-sm font-medium text-gray-500 hover:text-nokia-blue transition-colors flex items-center gap-1 group/link">
                      {t('forgotPassword')}
                      <span className="material-symbols-outlined text-base transition-transform group-hover/link:translate-x-0.5">arrow_forward</span>
                    </Link>
                  </motion.div>
                ) : (
                  <div className="flex justify-end">
                    <Link to="/forgot-password" className="text-sm font-medium text-gray-500 hover:text-nokia-blue transition-colors flex items-center gap-1 group/link">
                      {t('forgotPassword')}
                      <span className="material-symbols-outlined text-base transition-transform group-hover/link:translate-x-0.5">arrow_forward</span>
                    </Link>
                  </div>
                )}
              </AnimatePresence>

              {/* Login button */}
              <ButtonWrapper
                className={`relative overflow-hidden w-full text-white font-bold rounded-lg py-3 md:py-4 px-4 md:px-6 mt-6 transition-all duration-300 transform active:scale-[0.98] group border border-transparent ${
                  isLoading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                {...buttonProps}
                type="submit"
                disabled={isLoading || (requiresCaptcha && !recaptchaToken)}
              >
                <div className="absolute inset-0 w-full h-full bg-blue-nokia from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer"></div>
                <span className="relative z-10 flex items-center justify-center gap-2 tracking-wide text-sm md:text-base">
                  {t('login')}
                </span>
              </ButtonWrapper>
            </form>
            
            {/* Request access */}
            <div className="mt-8 text-center">
              <p className="text-gray-500 text-base">
                {t('newUser')}{' '}
                <Link to="/register" className="text-nokia-blue font-bold hover:text-primary-hover transition-all">
                  {t('createAccount')}
                </Link>
              </p>
            </div>
                </>
              )}
            
            {/* 
            
            <AnimatePresence>
              {useTransformersAnimation ? (
                <motion.div 
                  className="relative flex py-6 items-center"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: 0.25, ease: 'easeOut' }}
                  style={{ willChange: 'transform, opacity' }}
                >
                  <div className="flex-grow border-t border-gray-200"></div>
                  <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase tracking-widest font-semibold">{t('orAccessWith')}</span>
                  <div className="flex-grow border-t border-gray-200"></div>
                </motion.div>
              ) : (
                <div className="relative flex py-6 items-center">
                  <div className="flex-grow border-t border-gray-200"></div>
                  <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase tracking-widest font-semibold">{t('orAccessWith')}</span>
                  <div className="flex-grow border-t border-gray-200"></div>
                </div>
              )}
            </AnimatePresence>

            {useTransformersAnimation ? (
              <AnimatePresence>
                <motion.div 
                  className="grid grid-cols-1 gap-4"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: 0.3, ease: 'easeOut' }}
                  style={{ willChange: 'transform, opacity' }}
                >
                  <button
                    type="button"
                    className="flex-1 items-center justify-center gap-2 bg-gray-50 border border-nokia-blue text-gray-700 rounded-lg py-2.5 md:py-3 transition-all duration-200 shadow-md group flex"
                  >
                    <span className="material-symbols-outlined text-lg md:text-[20px] text-nokia-blue transition-colors">domain</span>
                    <span className="text-sm md:text-base font-medium text-nokia-blue transition-colors">SSO</span>
                  </button>
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                <button
                  type="button"
                  className="flex-1 items-center justify-center gap-2 bg-gray-50 border border-nokia-blue text-gray-700 rounded-lg py-3 transition-all duration-200 shadow-md group flex"
                >
                  <span className="material-symbols-outlined text-[20px] text-nokia-blue transition-colors">domain</span>
                  <span className="text-base font-medium text-nokia-blue transition-colors">SSO</span>
                </button>
              </div>
            )} 
            
            */}

            </div>
          </FormContainer>

          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-nokia-blue rounded-tl-2xl opacity-40 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
          {!isRsTenant && (
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-nokia-green rounded-br-2xl opacity-40 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
          )}
        </div>

      </main>
    </>
  );
};

export default Login;
